import React, { useState } from 'react';
import SEO from '../components/SEO';

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            category: "General",
            questions: [
                {
                    q: "What is FreeImageMathSolver?",
                    a: "FreeImageMathSolver is a free, AI-powered online tool that solves math problems by analyzing uploaded images. Simply upload a photo of your math problem, and our AI provides detailed, step-by-step solutions instantly."
                },
                {
                    q: "Is FreeImageMathSolver really free?",
                    a: "Yes! FreeImageMathSolver is 100% free with no hidden costs, subscriptions, or premium tiers. We believe quality math education should be accessible to everyone."
                },
                {
                    q: "Do I need to create an account?",
                    a: "No signup required! You can start solving math problems immediately without creating an account or providing any personal information."
                },
                {
                    q: "What types of math problems can it solve?",
                    a: "We support a wide range of math topics including Algebra, Geometry, Calculus, Trigonometry, Statistics, Pre-Calculus, and more. From basic arithmetic to advanced calculus, we've got you covered."
                }
            ]
        },
        {
            category: "Technical",
            questions: [
                {
                    q: "What image formats are supported?",
                    a: "We support JPG, PNG, and WEBP image formats. Maximum file size is 10MB per image."
                },
                {
                    q: "Can it read handwritten math problems?",
                    a: "Yes! Our advanced OCR (Optical Character Recognition) technology can read both handwritten and typed math problems. For best results, ensure your handwriting is clear and legible."
                },
                {
                    q: "How accurate are the solutions?",
                    a: "Our AI has a 99% accuracy rate. All solutions are verified before display. However, we recommend double-checking complex problems and using the solutions as a learning tool."
                },
                {
                    q: "How long does it take to get a solution?",
                    a: "Most problems are solved in 2-3 seconds! The AI analyzes your image, identifies the problem, and generates a step-by-step solution almost instantly."
                },
                {
                    q: "Can I use this on my mobile phone?",
                    a: "Absolutely! FreeImageMathSolver is fully responsive and works perfectly on smartphones, tablets, and desktop computers. You can even use your phone's camera to capture problems directly."
                }
            ]
        },
        {
            category: "Usage",
            questions: [
                {
                    q: "How do I upload an image?",
                    a: "You have multiple options: 1) Click 'Browse Files' to select from your device, 2) Click 'Take Photo' to use your camera, 3) Drag and drop an image onto the upload area, or 4) Press Ctrl+V to paste from clipboard."
                },
                {
                    q: "Can I download the solutions?",
                    a: "Yes! Every solution can be downloaded as a PDF file for offline study and future reference. Just click the 'Download as PDF' button below the solution."
                },
                {
                    q: "What if the solution is wrong?",
                    a: "While our AI is highly accurate, errors can occur. If you believe a solution is incorrect, try uploading a clearer image or rephrase the problem. You can also verify the solution using other resources."
                },
                {
                    q: "Can I solve multiple problems?",
                    a: "Yes! There's no limit to how many problems you can solve. Simply upload a new image after receiving your solution to solve another problem."
                }
            ]
        },
        {
            category: "Privacy & Security",
            questions: [
                {
                    q: "Is my data safe?",
                    a: "Yes! We take privacy seriously. Uploaded images are processed securely and are not stored permanently on our servers. We don't share your data with third parties."
                },
                {
                    q: "Do you store my uploaded images?",
                    a: "Images are temporarily stored only for processing and are automatically deleted after the solution is generated. We don't keep a permanent record of your uploads."
                },
                {
                    q: "Can teachers or parents see what I upload?",
                    a: "No. Your uploads are completely private. We don't track individual users or share upload history with anyone."
                }
            ]
        },
        {
            category: "Troubleshooting",
            questions: [
                {
                    q: "The AI couldn't read my problem. What should I do?",
                    a: "Try these tips: 1) Ensure good lighting when taking photos, 2) Keep the image clear and in focus, 3) Avoid shadows or glare, 4) Make sure the entire problem is visible, 5) For handwritten problems, write more clearly."
                },
                {
                    q: "The solution doesn't make sense. Why?",
                    a: "This could happen if the AI misread the problem. Try uploading a clearer image or typing the problem if possible. Also ensure the problem is complete and all necessary information is visible."
                },
                {
                    q: "Can I get help with word problems?",
                    a: "Yes! Our AI can handle word problems. Make sure the entire problem text is clearly visible in the image for best results."
                }
            ]
        }
    ];

    const allQuestions = faqs.flatMap((category, catIndex) =>
        category.questions.map((q, qIndex) => ({
            ...q,
            index: catIndex * 100 + qIndex
        }))
    );

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    // JSON-LD Schema for FAQ
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allQuestions.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.a
            }
        }))
    };

    return (
        <>
            <SEO
                title="FAQ - Frequently Asked Questions | FreeImageMathSolver"
                description="Find answers to common questions about FreeImageMathSolver. Learn how to use our AI math solver, supported topics, accuracy, privacy, and more."
                keywords="math solver FAQ, how to use math solver, math solver questions, AI math help FAQ, free math solver help"
                canonical="/faq"
            />

            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Hero */}
                    <div className="text-center mb-16">
                        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                            Frequently Asked <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Questions</span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Everything you need to know about FreeImageMathSolver
                        </p>
                    </div>

                    {/* FAQ Categories */}
                    {faqs.map((category, catIndex) => (
                        <div key={catIndex} className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                <div className="w-8 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
                                {category.category}
                            </h2>
                            <div className="space-y-4">
                                {category.questions.map((item, qIndex) => {
                                    const index = catIndex * 100 + qIndex;
                                    const isOpen = openIndex === index;

                                    return (
                                        <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-lg">
                                            <button
                                                onClick={() => toggleFAQ(index)}
                                                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                                            >
                                                <span className="font-semibold text-gray-900 text-lg">{item.q}</span>
                                                <svg
                                                    className={`w-6 h-6 text-indigo-600 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''
                                                        }`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            <div
                                                className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'
                                                    }`}
                                            >
                                                <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                                                    {item.a}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {/* Still Have Questions */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-xl p-8 md:p-12 text-center text-white mt-16">
                        <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
                        <p className="text-xl mb-6 text-indigo-100">
                            Can't find the answer you're looking for? We're here to help!
                        </p>
                        <button className="px-8 py-3 bg-white text-indigo-600 font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                            Contact Support
                        </button>
                    </div>
                </div>
            </div>

            {/* JSON-LD Schema */}
            <script type="application/ld+json">
                {JSON.stringify(faqSchema)}
            </script>
        </>
    );
};

export default FAQ;
